import { useMutation } from '@tanstack/react-query';
import { post } from '../api-client';

/**
 * POST /api/v1/Practice/PreBookSelectedPatient
 *
 * Mirrors Flutter `fetchPrebookSelectedPatientData()` →
 * `scheduling_data_source_impl.dart:870–909`. Fired after a patient
 * is selected (and provider + location are already set). Backend
 * returns:
 *   - patient            → full Patient profile (we already have id/name)
 *   - patientFlags       → auto-populates `appointment.flagList` when
 *                          local list is empty (Flutter line 2305–2307)
 *   - patientAddressList → drives the Residential Address section
 *                          (Home location only)
 *   - prices             → drives the invoice / billing breakdown in
 *                          the Payment section
 */

export interface PatientFlag {
  code?: number | string;
  display?: string;
  categoryName?: string;
}

/**
 * Picker-list element — Shape A, the merged "home-visit destinations"
 * list (patient residential addresses + emergency contacts as potential
 * visit destinations, each tagged with `isResidential` and `relation`).
 *
 * Verified against dev curl 2026-05-20 (Andrew): the `patientAddressList[]`
 * inside `PreBookSelectedPatient` response ships 16 fields, all
 * Flutter-shape. Mirrors Flutter `AppointmentPatientAddress` in
 * `appointment_patient_address.dart`.
 *
 * Canonical wire sample (from the 2026-05-20 dev curl — keep this in
 * sync if the backend ever changes the shape; mismatch here silently
 * re-breaks the picker the same way the old invented fields did):
 *
 *   {
 *     "id": "a5626b03-588c-42a0-6a22-08dd67130a77",
 *     "name": "Andrew Safwat",
 *     "imageUrl": null,
 *     "building": "",
 *     "apartment": "",
 *     "country": "",
 *     "city": "",
 *     "address": "",
 *     "latitude": null,
 *     "longitude": null,
 *     "isDefault": false,
 *     "phoneNumber": null,
 *     "relation": { "id": "6799dd4e-bab8-e711-811d-0025903a03a1",
 *                   "name": "Father" },
 *     "locationName": "",
 *     "notes": null,
 *     "isResidential": false
 *   }
 *
 * Every field below maps 1:1 to a key in that sample. Every key in
 * that sample appears as a field below. If you add a field, document
 * which curl run added it. If you remove a field, capture a fresh
 * curl proving the backend dropped it.
 *
 * NOT the same as `PatientAddress` in `src/types/patient-api.ts:230` —
 * that's Shape B (the patient address-book, 13 fields, `contactId`
 * keyed). The two coexist because the backend exposes the same address
 * data through two endpoint shapes:
 *
 *   GET / PUT /api/Patients/{id}              → Shape B (residential only)
 *   POST /api/v1/Practice/PreBookSelectedPatient
 *     → patientAddressList[]                  → Shape A (merged w/ contacts)
 *   GET /api/Patients/PatientAddressList      → Shape A (same parser)
 *
 * Title-source rule (per Flutter `residential_address_widget.dart`):
 *   - `isResidential === true` → render `locationName` as bold title,
 *     use patient.photoUrl for avatar
 *   - `isResidential === false` → render `name` as bold title, use
 *     `imageUrl` for avatar
 */
export interface PatientAddress {
  /** Address GUID. Used by selection-by-id on refetch. */
  id?: string;
  /** Display name for non-residential entries (emergency contact's full
   *  name). Title-source rule above. */
  name?: string;
  /** Photo URL for emergency-contact entries; residentials reuse
   *  patient.photoUrl. */
  imageUrl?: string | null;
  building?: string;
  apartment?: string;
  country?: string;
  city?: string;
  /** Street address (the autocomplete output from Google Places). */
  address?: string;
  /** Stringified coords — Flutter wire convention. */
  latitude?: string | null;
  longitude?: string | null;
  isDefault?: boolean;
  phoneNumber?: string | null;
  /** Master-list relation entry. Lowercase keys here (Flutter's `Relation`
   *  class). NOTE: emergency-contact saves on `/Patients/EmergencyContact`
   *  ship PascalCase `{Id, Name, System, Code}` — separate code path. */
  relation?: { id?: string; name?: string };
  /** Bold title for residential entries (the user-supplied label, e.g.
   *  "Home", "Office"). */
  locationName?: string;
  notes?: string | null;
  /** True for the patient's own residential addresses; false for emergency
   *  contacts surfaced as visit destinations. Drives the title-source +
   *  avatar-source branch. */
  isResidential?: boolean;
}

/**
 * Form-level "Invoice" shape — uses Flutter's `Invoice` class field names
 * (`grossTotal` / `discountTotal` / `netTotal` / `patientPayable` /
 * `sponsorOutstanding`). The PreBookSelectedPatient and PreBookCalculatePrice
 * endpoints both ship a different raw shape (`gross / discount / net /
 * payable / sponsorAmount`); callers normalise to this interface so the UI
 * doesn't have to know about either backend shape.
 */
export interface InvoicePrices {
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientPayable?: number;
  sponsorOutstanding?: number;
}

/**
 * Raw shape returned by `/api/v1/Practice/PreBookCalculatePrice` and
 * embedded under `PreBookSelectedPatient.prices`. Mirrors Flutter
 * `calculate_sponsor_amount_response.dart`.
 */
export interface RawCalculateSponsorAmountResponse {
  gross?: number;
  discount?: number;
  net?: number;
  patientAmount?: number;
  sponsorAmount?: number;
  payable?: number;
  serviceId?: string;
}

/** Map the wire shape to the form-level Invoice shape. */
export function toInvoicePrices(
  raw: RawCalculateSponsorAmountResponse | undefined | null,
): InvoicePrices | null {
  if (!raw) return null;
  return {
    grossTotal: raw.gross,
    discountTotal: raw.discount,
    netTotal: raw.net,
    // Flutter `payment_widget.dart:280` writes `invoice.patientPayable =
    // amountResponse.payable` — NOT `patientAmount`.
    patientPayable: raw.payable,
    sponsorOutstanding: raw.sponsorAmount,
  };
}

export interface EmergencyContact {
  id?: string;
  emergencyContactId?: string;
  patientId?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /**
   * For existing rows the backend stores this as the relation UUID; for
   * display it is sometimes the relation name. The save endpoint requires
   * the UUID round-tripped verbatim, so we preserve it raw.
   */
  relationship?: string;
  /** Some payloads use `relationshipName`. */
  relationshipName?: string;
  /**
   * Master-list relation entry. Backend ships PascalCase keys (Id/Name/
   * System/Code) but `useRelationships` normalises to lowercase before it
   * reaches the form; we keep both shapes optional so either source works.
   */
  relation?: {
    id?: string;
    name?: string;
    code?: string;
    system?: string;
  };
  phone?: string;
  contactNumber?: string;
  photo?: string;
  /** Backend ships `photoUrl` (Flutter EmergencyContactList:35). */
  photoUrl?: string;
  /** Marks the patient themselves as a contact (Flutter shows "Self"). */
  isSelf?: boolean;
  /**
   * Round-tripped on save so the backend's IsEmergencyAddressChanged path
   * has all the fields it dereferences without NREing.
   */
  submittedDate?: string | null;
  building?: string | null;
  apartment?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  latitude?: string | null;
  longitude?: string | null;
}

/**
 * Insurance + Corporate models — Flutter
 * `patient_module/.../insurance_model.dart` (InsuranceList,
 * PayerBillingGroup, PayerDetails). The save flow re-ships the picked
 * sponsor's `insuranceProvider` / `corporateProvider` object as
 * `appointment.sponsor`, plus `policyNumber` as `appointment.policyNo`.
 */
export interface PayerBillingGroup {
  billingGroupId?: string;
  category?: string;
  // Nullable: PayerCopayTable clears a blanked field to null rather than
  // coercing it to 0. Declared `number` here, the `as Partial<T>` cast in
  // that component would hide the mismatch instead of surfacing it.
  value?: number | null;
  maximumCopayment?: number | null;
}

/** TPA entry on a payer (`PatientPayerDetails.relatedTpa[]`). The patient
 *  HealthInsuranceList / CorporateAgreementList + PreBook responses all ship
 *  this shape; `code` feeds the "TPA: {code}" line, `companyName` the
 *  TPA-mediated dropdown title `{companyName} ({payerName})`. */
export interface PayerTpa {
  id?: string;
  companyName?: string;
  code?: string;
}

export interface PayerDetails {
  id?: string;
  payerName?: string;
  imageUrl?: string;
  /** Some endpoints lowercase / pascalcase the same field. */
  name?: string;
  /** Payer's billing company code — shown as "Insurance: {payerCompanyId}".
   *  Present on the HealthInsuranceList / PreBook payer envelope (verified
   *  via the 2026-06-08 dev probe). */
  payerCompanyId?: string;
  /** Agreement code + display name — the search dropdown shows
   *  `payerAgreementName` (e.g. "GN+") as the green agreement line. From
   *  `/api/Billing/Agreements/InsurancePayerDetails` (2026-06-08 probe). */
  payerAgreementId?: string;
  payerAgreementName?: string;
  /** Related TPAs — `relatedTpa[0]` drives the "TPA: {code}" line and the
   *  TPA-mediated title `{companyName} ({payerName})`. */
  relatedTpa?: PayerTpa[];
}

export interface HealthInsurance {
  healthInsuranceId?: string;
  patientId?: string;
  insuranceProviderId?: string;
  insuranceProvider?: PayerDetails;
  policyNumber?: string;
  frontPhotoUrl?: string | null;
  backPhotoUrl?: string | null;
  eligiblityStartDate?: string | null;
  expirationDate?: string | null;
  submittedDate?: string | null;
  isPrimary?: boolean;
  isReviewed?: boolean;
  isVerified?: boolean;
  /** Soft-delete flag — Flutter `InsuranceList.isDeleted`. The save
   * endpoint accepts rows with `isDeleted: true && healthInsuranceId`
   * set and tombstones them server-side. */
  isDeleted?: boolean;
  copaymentType?: string | null;
  billingGroups?: PayerBillingGroup[];
  agreementId?: string;
  agreementName?: string;
}

export interface CorporateAgreement {
  corporateAgreementId?: string;
  patientId?: string;
  corporateProviderId?: string;
  corporateProvider?: PayerDetails;
  policyNumber?: string;
  frontPhotoUrl?: string | null;
  backPhotoUrl?: string | null;
  eligiblityStartDate?: string | null;
  expirationDate?: string | null;
  submittedDate?: string | null;
  isPrimary?: boolean;
  isReviewed?: boolean;
  isVerified?: boolean;
  copaymentType?: string | null;
  billingGroups?: PayerBillingGroup[];
  agreementId?: string;
  agreementName?: string;
}

export interface PreBookSelectedPatientResponse {
  /** Full patient profile — when present we read photo / dob / age / gender / importance / emergencyContactResponse. */
  patient?: {
    photo?: string;
    image?: string;
    photoUrl?: string;
    dob?: string;
    birthDate?: string;
    age?: string;
    gender?: string | { code?: string; display?: string };
    importance?: { code?: string; display?: string };
    isMissingData?: boolean;
    /** Flutter writes `appointment.emergencyContactDataList = state.response.patient?.emergencyContactResponse` (create_appointment_widget.dart:2326–2327). */
    emergencyContactResponse?: EmergencyContact[];
    /** Older payload key — kept for compatibility. */
    emergencyContactDataList?: EmergencyContact[];
    /** Flutter `Patient.healthInsuranceResponse: List<InsuranceList>` — drives the Insurance tab carousel. */
    healthInsuranceResponse?: HealthInsurance[];
    /** Flutter `Patient.corporateAgreementResponse: List<CorporateList>` — drives the Corporate tab carousel. */
    corporateAgreementResponse?: CorporateAgreement[];
  };
  patientFlags?: PatientFlag[];
  patientAddressList?: PatientAddress[];
  /** Some endpoints flatten the list onto the root response. */
  emergencyContactResponse?: EmergencyContact[];
  emergencyContactDataList?: EmergencyContact[];
  healthInsuranceResponse?: HealthInsurance[];
  corporateAgreementResponse?: CorporateAgreement[];
  /** Raw price breakdown — Flutter `CalculateSponsorAmountResponse` shape. */
  prices?: RawCalculateSponsorAmountResponse;
  /** PROD-3347: why `prices` has no patient co-payment (read with `toCoPaymentWarning`). */
  coPaymentWarning?: unknown;
}

interface PreBookPatientParams {
  patientId: string;
  providerId: string;
  locationId: string;
  billingProfileId?: string | null;
  /** Flutter sends "Self-pay" by default after a patient is picked. */
  paymentType?: 'Self-pay' | 'Insurance' | 'Corporate';
  sponsorId?: string | null;
  invoiceId?: string | null;
}

export function usePreBookSelectedPatient() {
  return useMutation<PreBookSelectedPatientResponse, Error, PreBookPatientParams>({
    mutationFn: (params) => {
      const paymentType = params.paymentType ?? 'Self-pay';
      const body = {
        patientId: params.patientId,
        billingProfileId: params.billingProfileId ?? '',
        locationId: params.locationId,
        sponsorId: params.sponsorId ?? '',
        paymentType,
        providerId: params.providerId,
        invoiceId: params.invoiceId ?? '',
        individualServices: [],
        patientCoPayment: {
          billingGroups: [],
          coPaymentType: paymentType,
          individualServices: [],
        },
      };
      return post<PreBookSelectedPatientResponse>(
        '/api/v1/Practice/PreBookSelectedPatient',
        body,
      );
    },
  });
}
